/* TARA EDITION — edition-number picker.
   Markup: snippets/te-number-grid.liquid   Styles: assets/te-numbers.css
   Strings: tara.num.* in the locale files, read from the JSON inside the element.

   Every scarf is one of 200, and each number is a variant with one unit of stock.

   One number (data-max="1", the launch default)
     buy       a free number at random is preselected ("Pick for me"); typing a
               number or choosing in the list replaces it. The solid button
               submits a real form to /cart/add after re-reading availability,
               so a number sold while the page was open is swapped before the
               post, and Shopify's own add-to-cart event fires.
     interest  (pre-launch) nothing is preselected; Done hands the choice to the
               page as te:numbers-chosen. Every change is sent as te:numbers-change.

   Several numbers (data-max > 1): chips, "add another", Done. Kept for later;
   in buy mode it adds through /cart/add.js and opens the cart drawer. */

const ROUTES = (window.Theme && window.Theme.routes) || {};
const ADD_URL = ROUTES.cart_add_url || '/cart/add.js';
const CART_URL = ROUTES.cart_url || '/cart';

const pad = (n) => String(n).padStart(3, '0');

class TENumberPicker extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.mode = this.dataset.mode || 'buy';
    this.max = Number(this.dataset.max) || 1;
    this.single = this.max === 1;
    this.unit = Number(this.dataset.unit) || 0;
    this.currency = this.dataset.currency || 'EUR';
    this.selected = new Map();
    this.bagged = new Set();
    this.busy = false;
    this.choosing = true;
    this.browsing = false;
    this.auto = false;

    try {
      this.i18n = JSON.parse(this.querySelector('script[data-te-num-i18n]')?.textContent || '{}');
    } catch (e) {
      this.i18n = {};
    }

    this.grid = this.querySelector('.te-num__grid');
    this.finder = this.querySelector('[data-te-finder]');
    this.browse = this.querySelector('[data-te-browse]');
    this.browseBtn = this.querySelector('[data-te-browse-btn]');
    this.rangesEl = this.querySelector('[data-te-ranges]');
    this.input = this.querySelector('[data-te-input]');
    this.goBtn = this.querySelector('[data-te-go]');
    this.hint = this.querySelector('[data-te-hint]');
    this.stateEl = this.querySelector('[data-te-state]');
    this.summary = this.querySelector('[data-te-summary]');
    this.panel = this.querySelector('[data-te-panel]');
    this.chips = this.querySelector('[data-te-chips]');
    this.moreBtn = this.querySelector('[data-te-more]');
    this.addBtn = this.querySelector('[data-te-add]');
    this.form = this.querySelector('[data-te-buy-form]');
    this.variantInput = this.querySelector('[data-te-variant]');
    this.msg = this.querySelector('[data-te-msg]');
    this.leftEl = this.querySelector('[data-te-left]');
    this.pickBtn = this.querySelector('[data-te-pick]');
    if (!this.grid || !this.addBtn) return;

    this.cells = Array.from(this.grid.querySelectorAll('.te-num__cell'));
    this.byNum = new Map(this.cells.map((c) => [parseInt(c.dataset.n, 10), c]));
    this.buildRanges();

    this.grid.addEventListener('click', (e) => {
      const cell = e.target.closest('.te-num__cell');
      if (cell && !cell.disabled) this.toggle(cell);
    });
    this.grid.addEventListener('keydown', (e) => this.onKey(e));
    this.pickBtn?.addEventListener('click', () => this.pickForMe());
    this.browseBtn?.addEventListener('click', () => {
      this.browsing = !this.browsing;
      this.render();
    });
    this.rangesEl?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-te-range]');
      if (b) this.showRange(Number(b.dataset.teRange));
    });
    this.input?.addEventListener('input', () => {
      const clean = this.input.value.replace(/\D/g, '').slice(0, 3);
      if (clean !== this.input.value) this.input.value = clean;
      this.check();
    });
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.chooseTyped();
      }
    });
    this.goBtn?.addEventListener('click', () => this.chooseTyped());
    this.hint?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-te-suggest]');
      if (b) this.choose(this.byNum.get(Number(b.dataset.teSuggest)));
    });
    this.moreBtn?.addEventListener('click', () => this.more());
    this.chips?.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-te-chip]');
      if (chip) this.remove(chip.dataset.teChip);
    });
    if (this.form) this.form.addEventListener('submit', (e) => this.submitForm(e));
    else this.addBtn.addEventListener('click', () => this.submit());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.mode === 'buy') this.refresh();
    });

    this.countLeft();
    this.restore();
    // From the Register: ?number=46 chooses N° 046 when it is free
    const wanted = this.mode === 'buy' && Number(new URLSearchParams(window.location.search).get('number'));
    const wantedCell = wanted && this.byNum.get(wanted);
    if (wantedCell && !wantedCell.disabled) {
      if (this.single) this.selected.clear();
      this.choose(wantedCell);
    } else if (wanted) {
      this.say(this.t(wantedCell ? 'is_taken' : 'out_of_range', { n: pad(wanted), max: pad(this.cells.length) }));
    }
    // Buy, one number: arrive with a free number chosen at random
    if (this.single && this.mode === 'buy' && !this.selected.size) this.pickForMe(true);
    this.showRange(this.rangeOf(this.firstSelectedCell() || this.cells.find((c) => !c.disabled)));
    // Interest mode starts empty and quiet; the page then sets the numbers saved for the address
    this.silent = this.mode === 'interest';
    this.render();
    this.silent = false;
    this.dispatchEvent(new CustomEvent('te:numbers-ready', { bubbles: true }));
  }

  /* "Number {n} …" → filled from the locale strings */
  t(key, vars = {}) {
    let s = this.i18n[key] || '';
    for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(v);
    return s;
  }

  free(cell) {
    return cell && !cell.disabled && !this.selected.has(cell.dataset.id);
  }

  /* The range only ("Numbers 001–200"); no counters */
  countLeft() {
    if (!this.leftEl || !this.cells.length) return;
    this.leftEl.textContent = this.t('range', { from: this.cells[0].dataset.n, to: this.cells[this.cells.length - 1].dataset.n });
  }

  /* ---- the full list, fifty at a time ---- */
  buildRanges() {
    this.span = 50;
    this.range = 0;
    if (!this.rangesEl) return;
    if (this.cells.length <= 60) {
      this.rangesEl.hidden = true;
      this.span = this.cells.length;
      return;
    }
    const count = Math.ceil(this.cells.length / this.span);
    this.rangesEl.innerHTML = Array.from({ length: count }, (_, i) => {
      const lo = i * this.span + 1;
      const hi = Math.min((i + 1) * this.span, this.cells.length);
      return `<button type="button" class="te-num__range" data-te-range="${i}" aria-pressed="false"><span>${pad(lo)}–${pad(hi)}</span><small data-te-range-free></small></button>`;
    }).join('');
  }

  rangeOf(cell) {
    const i = cell ? this.cells.indexOf(cell) : 0;
    return i < 0 ? 0 : Math.floor(i / this.span);
  }

  firstSelectedCell() {
    const id = this.selected.keys().next().value;
    return id ? this.grid.querySelector(`.te-num__cell[data-id="${id}"]`) : null;
  }

  showRange(i) {
    this.range = i;
    this.cells.forEach((c, j) => {
      c.hidden = Math.floor(j / this.span) !== i;
    });
    this.rangesEl?.querySelectorAll('[data-te-range]').forEach((b) => {
      b.setAttribute('aria-pressed', Number(b.dataset.teRange) === i ? 'true' : 'false');
    });
  }

  countRanges() {
    this.rangesEl?.querySelectorAll('[data-te-range]').forEach((b) => {
      const i = Number(b.dataset.teRange);
      const n = this.cells.slice(i * this.span, (i + 1) * this.span).filter((c) => this.free(c)).length;
      b.querySelector('[data-te-range-free]').textContent = this.t('free_in_range', { n });
    });
  }

  /* ---- typing a number ---- */
  check() {
    if (!this.hint) return null;
    const raw = this.input.value;
    this.hint.innerHTML = '';
    this.hint.dataset.state = '';
    if (!raw) return null;
    const n = parseInt(raw, 10);
    const cell = this.byNum.get(n);
    if (!cell) {
      this.hint.dataset.state = 'no';
      this.hint.textContent = this.t('out_of_range', { max: pad(this.cells.length) });
      return null;
    }
    if (this.selected.has(cell.dataset.id)) {
      this.hint.textContent = this.t('already_chosen', { n: cell.dataset.n });
      return null;
    }
    if (!cell.disabled) {
      this.hint.dataset.state = 'ok';
      this.hint.textContent = this.t('is_free', { n: cell.dataset.n });
      return cell;
    }
    const why = cell.classList.contains('te-num__cell--house')
      ? 'is_house'
      : cell.classList.contains('te-num__cell--bagged')
        ? 'is_in_basket'
        : 'is_taken';
    const near = this.nearest(n);
    this.hint.dataset.state = 'no';
    this.hint.innerHTML =
      this.escape(this.t(why, { n: cell.dataset.n })) +
      (near.length
        ? ` ${this.escape(this.t('nearest'))} ${near
            .map((c) => `<button type="button" class="te-num__suggest" data-te-suggest="${parseInt(c.dataset.n, 10)}">N° ${c.dataset.n}</button>`)
            .join(' ')}`
        : '');
    return null;
  }

  escape(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  /* The closest free number on each side of n */
  nearest(n) {
    const out = [];
    for (const step of [-1, 1]) {
      for (let k = n + step; this.byNum.has(k); k += step) {
        if (this.free(this.byNum.get(k))) {
          out.push(this.byNum.get(k));
          break;
        }
      }
    }
    return out;
  }

  chooseTyped() {
    const cell = this.check();
    if (cell) this.choose(cell);
    else if (!this.input.value) this.input.focus();
  }

  /* ---- selection ---- */
  choose(cell, auto = false) {
    if (!cell || cell.disabled) return;
    if (this.single) {
      this.selected.clear();
    } else if (this.selected.has(cell.dataset.id)) {
      return;
    } else if (this.selected.size >= this.max) {
      this.say(this.t('max', { max: this.max }));
      return;
    }
    this.selected.set(cell.dataset.id, cell.dataset.n);
    this.auto = auto;
    this.choosing = false;
    if (this.input) this.input.value = '';
    this.check();
    this.say('');
    this.showRange(this.rangeOf(cell));
    this.render();
    if (!auto && !this.single) this.addBtn.focus({ preventScroll: true });
  }

  toggle(cell) {
    if (this.selected.has(cell.dataset.id) && !this.single) this.remove(cell.dataset.id);
    else this.choose(cell);
  }

  remove(id) {
    this.selected.delete(id);
    if (!this.selected.size) this.choosing = true;
    this.say('');
    this.render();
  }

  more() {
    this.choosing = true;
    this.say('');
    this.render();
    this.input?.focus({ preventScroll: true });
  }

  /* A free number at random, so "Pick for me" does not send everyone to the same low number */
  pickForMe(quiet = false) {
    const free = this.cells.filter((c) => !c.disabled && (this.single || !this.selected.has(c.dataset.id)));
    const cell = free[Math.floor(Math.random() * free.length)];
    if (!cell) {
      if (!quiet) this.say(this.t('all_taken'));
      return;
    }
    this.choose(cell, true);
  }

  numbers() {
    return Array.from(this.selected.values()).sort();
  }

  /* ---- keyboard: arrows move across the visible fifty ---- */
  onKey(e) {
    const cells = this.cells.filter((c) => !c.hidden);
    const i = cells.indexOf(document.activeElement);
    if (i < 0) return;
    const cols = getComputedStyle(this.grid).gridTemplateColumns.split(' ').length || 10;
    const jump = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[e.key];
    if (jump === undefined) return;
    e.preventDefault();
    const next = cells[Math.max(0, Math.min(cells.length - 1, i + jump))];
    next?.focus();
  }

  /* ---- rendering ---- */
  fmt(cents) {
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  }

  list(nums) {
    return nums.map((n) => `N° ${n}`).join(', ');
  }

  render() {
    this.grid.querySelectorAll('.te-num__cell').forEach((c) => {
      c.setAttribute('aria-pressed', this.selected.has(c.dataset.id) ? 'true' : 'false');
    });
    const n = this.selected.size;
    const nums = this.numbers();

    if (this.single) {
      this.pickBtn?.setAttribute('aria-pressed', this.auto && n ? 'true' : 'false');
      if (this.stateEl) {
        this.stateEl.textContent = n
          ? this.t('state', { n: nums[0], total: pad(this.cells.length), design: (this.dataset.title || '').toUpperCase() })
          : '';
      }
      if (this.variantInput) this.variantInput.value = n ? this.selected.keys().next().value : '';
    } else if (this.summary) {
      const each = this.fmt(this.unit);
      if (this.mode === 'interest') {
        this.summary.textContent = n === 1 ? this.t('summary_one') : this.t('summary_many', { n });
      } else {
        this.summary.textContent =
          n === 1 ? this.t('summary_price_one', { price: each }) : this.t('summary_price_many', { n, price: each, sum: this.fmt(this.unit * n) });
      }
      if (this.chips) {
        this.chips.innerHTML = Array.from(this.selected.entries())
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([id, num]) => `<button type="button" class="te-num__chip" data-te-chip="${id}" aria-label="${this.escape(this.t('remove', { n: num }))}">N° ${num} <span aria-hidden="true">×</span></button>`)
          .join('');
      }
      const closed = n > 0 && !this.choosing;
      if (this.finder) this.finder.hidden = closed;
      if (this.panel) this.panel.hidden = n === 0;
      if (this.moreBtn) this.moreBtn.hidden = !closed || n >= this.max;
    }

    if (this.browse) this.browse.hidden = !this.browsing;
    if (this.browseBtn) {
      this.browseBtn.setAttribute('aria-expanded', this.browsing ? 'true' : 'false');
      this.browseBtn.textContent = this.browsing ? this.t('hide_all') : this.t('see_all');
    }
    // Interest mode (pre-launch): every change reaches the page, so the list form always carries the current choice
    if (this.mode === 'interest' && !this.silent) {
      this.dispatchEvent(
        new CustomEvent('te:numbers-change', {
          bubbles: true,
          detail: { handle: this.dataset.handle, title: this.dataset.title, numbers: nums },
        })
      );
    }
    this.addBtn.disabled = n === 0 || this.busy;
    this.countRanges();
    this.save();
  }

  say(text) {
    if (!this.msg) return;
    this.msg.textContent = text;
    this.msg.hidden = !text;
  }

  /* ---- buy, one number: a real form post, checked first ---- */
  async submitForm(e) {
    if (this.verified) return; // second pass: let the browser post it
    e.preventDefault();
    if (this.busy || !this.selected.size) return;
    this.busy = true;
    const label = this.addBtn.textContent;
    this.addBtn.textContent = this.t('adding');
    this.addBtn.disabled = true;
    const before = this.selected.keys().next().value;
    let checked = true;
    try {
      checked = await this.refresh();
    } catch (err) {
      checked = true; // cannot check: let the cart decide
    }
    this.busy = false;
    this.addBtn.textContent = label;
    const now = this.selected.keys().next().value;
    if (checked && now && now === before) {
      this.verified = true;
      this.variantInput.value = now;
      this.addBtn.disabled = true;
      this.addBtn.textContent = this.t('adding');
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.submit();
      return;
    }
    this.render();
  }

  /* ---- interest, or buy with several numbers ---- */
  async submit() {
    if (this.busy || !this.selected.size) return;
    if (this.mode === 'interest') {
      // Nothing to buy yet: hand the choice to the page (pre-launch list form)
      this.dispatchEvent(
        new CustomEvent('te:numbers-chosen', {
          bubbles: true,
          detail: { handle: this.dataset.handle, title: this.dataset.title, numbers: this.numbers() },
        })
      );
      this.choosing = false;
      this.render();
      return;
    }
    this.busy = true;
    this.addBtn.disabled = true;
    const label = this.addBtn.textContent;
    this.addBtn.textContent = this.t('adding');
    this.say('');

    const items = Array.from(this.selected.keys()).map((id) => ({ id: Number(id), quantity: 1 }));
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    let data;
    try {
      const res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(sections ? { items, sections } : { items }),
      });
      data = await res.json();
      if (!res.ok || data.status) throw data;
    } catch (err) {
      this.busy = false;
      this.addBtn.textContent = label;
      await this.refresh(err);
      return;
    }

    const added = this.numbers();
    for (const id of this.selected.keys()) {
      const cell = this.grid.querySelector(`.te-num__cell[data-id="${id}"]`);
      if (cell) {
        cell.disabled = true;
        cell.classList.add('te-num__cell--bagged');
        cell.setAttribute('aria-label', this.t('cell_in_basket', { n: cell.dataset.n }));
        this.bagged.add(id);
      }
    }
    this.selected.clear();
    this.choosing = true;
    this.busy = false;
    this.addBtn.textContent = label;
    this.render();
    this.say(this.t('added', { list: this.list(added) }));
    this.openDrawer(items, data);
  }

  /* Hand the result to the theme so its cart drawer opens; otherwise go to the cart page. */
  async openDrawer(items, data) {
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
      const cart = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'te-number-picker',
          sourceId: this.id,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (e) {
      console.warn('te-number-picker: cart drawer unavailable, opening the cart page', e);
      window.location.assign(CART_URL);
    }
  }

  /* Re-read availability from the product JSON. `err` is a failed cart response.
     Returns false when the chosen number has just gone (one number: the next free one is chosen instead). */
  async refresh(err) {
    const url = this.dataset.productUrl;
    if (!url || this.mode === 'interest') return true;
    const product = await fetch(`${url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } }).then((r) => {
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    }).catch(() => null);
    if (!product) {
      if (err) this.say(this.t('error'));
      return true;
    }
    const gone = [];
    for (const v of product.variants || []) {
      const id = String(v.id);
      const cell = this.grid.querySelector(`.te-num__cell[data-id="${id}"]`);
      if (!cell || cell.classList.contains('te-num__cell--house') || this.bagged.has(id)) continue;
      const taken = !v.available;
      if (taken && this.selected.has(id)) {
        gone.push(cell.dataset.n);
        this.selected.delete(id);
      }
      cell.disabled = taken;
      cell.classList.remove('te-num__cell--bagged');
      if (taken) cell.setAttribute('aria-label', this.t('cell_taken', { n: cell.dataset.n }));
      else cell.removeAttribute('aria-label');
    }
    if (gone.length && this.single) {
      const wasAuto = this.auto;
      this.pickForMe(true);
      const next = this.numbers()[0];
      this.say(next ? this.t('gone_single', { n: gone[0], next }) : this.t('all_taken'));
      if (!wasAuto) this.auto = false;
      this.render();
      return false;
    }
    this.render();
    if (gone.length) {
      this.say(this.t(gone.length > 1 ? 'gone_many' : 'gone_one', { list: this.list(gone) }));
      return false;
    }
    if (err) this.say((err && (err.description || err.message)) || this.t('error'));
    return true;
  }

  /* Interest mode: the page sets the numbers saved for an email address.
     silent: no te:numbers-change event (the page already knows). */
  setNumbers(nums, silent) {
    if (!this.grid) return;
    this.selected.clear();
    (nums || []).forEach((n) => {
      const cell = this.byNum.get(parseInt(n, 10));
      if (cell && !cell.disabled && this.selected.size < this.max) this.selected.set(cell.dataset.id, cell.dataset.n);
    });
    this.choosing = this.selected.size === 0;
    this.silent = !!silent;
    this.render();
    this.silent = false;
  }

  /* ---- keep the choice for the session ---- */
  key() {
    return `te-num:${this.id}`;
  }

  save() {
    if (this.mode === 'interest') return; // the page keeps interest choices per email address
    try {
      sessionStorage.setItem(this.key(), JSON.stringify({ auto: this.auto, picks: Array.from(this.selected.entries()) }));
    } catch (e) {
      /* storage may be blocked */
    }
  }

  restore() {
    if (this.mode === 'interest') return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.key()) || 'null');
      if (!saved || !Array.isArray(saved.picks)) return;
      for (const [id, n] of saved.picks) {
        const cell = this.grid.querySelector(`.te-num__cell[data-id="${id}"]`);
        if (cell && !cell.disabled) this.selected.set(id, n);
        if (this.single && this.selected.size) break;
      }
      // An automatic pick is redone on each visit, so it is always a number that is still free
      if (saved.auto && this.single && this.mode === 'buy') this.selected.clear();
      if (this.selected.size) this.choosing = false;
    } catch (e) {
      /* ignore */
    }
  }
}

if (!customElements.get('te-number-picker')) customElements.define('te-number-picker', TENumberPicker);
